// @flow
import * as React from 'react';
import { Box } from 'grommet';
import DisconnectedPipesList from 'esm-web-pro/containers/pages/network/objects/point11/DisconnectedPipesList';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import TransitPipesList from 'esm-web-pro/containers/pages/network/objects/point11/TransitPipesList';
import UseHighlightPoint from 'esm-web-pro/hooks/useHighlightPoint';
import { numberPointsCallbacksPipe } from 'esm-web-pro/containers/pages/network/objects/pipe/Pipe';
import UseHighlightLine from 'esm-web-pro/hooks/useHighlightLine';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import type { Map } from 'esm-web-pro/containers/map/types';
import type {
    VisualisedContinuousPipe,
    VisualisedPipe,
} from 'esm-web-pro/containers/pages/network/objects/gisline/GisLineContent';
import { withLeaflet } from 'react-leaflet';
import type { LeafletContext } from 'react-leaflet/lib/types';
import PipeConnectSidepanel, { PIPE_CONNECT_KEY } from
    'esm-web-pro/containers/pages/network/objects/point11/PipeConnectSidepanel';
import { getSidePanel } from 'esm-web-pro/redux/side-panel';
import { bindActionCreators } from 'redux';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import { connect } from 'react-redux';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import type { UUID } from 'esm-web-pro/types/common';
import Spinner from 'esm-web-pro/components/forms/Spinner';
import { FormattedMessage } from 'react-intl';

type SidePanelActionProps = {
    openPanel: (key: string, url: ?string, skipSensitivity: boolean) => { key: string, url: string },
    closePanel: (key: string, skipSensitivity?: boolean) => { key: string },
}

type Props = {
    ...$Exact<SidePanelActionProps>,
    point: Object,
    editor: BaseEditor,
    onView: Function,
    onRowClick: Function,
    clickedRow: ?string,
    map: Object,
    getPipePath: (id: string, map: Map) => Promise<VisualisedPipe>,
    getContinuousPipePath: (id: string, map: Map) => Promise<VisualisedContinuousPipe>,
    split: (id: UUID, data: { point: UUID }) => Promise<Object>,
    leaflet: LeafletContext,
    loading?: boolean,
    reload: Function,
    loadedData: boolean,
};

const Point11PipesList = (props: Props) => {
    const {
        point, point: {
            disconnected_continuous_pipes: disconnectedContinuousPipes,
            transit_pipes: transitPipes, disconnected_non_continuous_pipes: disconnectedNonContinuousPipes,
        }, map: networkMap, leaflet: { map }, editor, onView, onRowClick, clickedRow, getContinuousPipePath,
        getPipePath, openPanel, closePanel, loading, connectContinuousPipe, connectPipe, reload,
        loadedData: loadedPoint11Data, split,
    } = props;

    const [connectingPipe, setConnectingPipe] = React.useState(null);

    async function highlightPipeCallbacks(isContinuous, pipe) {
        let vPipe;
        let pointCallbacks;
        if (isContinuous) {
            vPipe = await getContinuousPipePath(pipe.uuid, networkMap);
            let startNumber = vPipe.start_point.sequence;
            if (vPipe.start_point.type === 'pop' && vPipe.start_point.alias) {
                startNumber = vPipe.start_point.alias;
            }
            let endNumber = vPipe.end_point.sequence;
            if (vPipe.end_point.type === 'pop' && vPipe.end_point.alias) {
                endNumber = vPipe.end_point.alias;
            }
            pointCallbacks = [
                UseHighlightPoint(vPipe.start_point.coordinates.coordinates, startNumber),
                UseHighlightPoint(vPipe.end_point.coordinates.coordinates, endNumber),
            ];
        } else {
            vPipe = await getPipePath(pipe.identity, networkMap);
            pointCallbacks = numberPointsCallbacksPipe(vPipe);
        }

        const coordinates = vPipe.path;
        const pipeCoords = coordinates.map((c) => [c[1], c[0]]);
        const highlightPipeCallback = UseHighlightLine(pipeCoords,
            [{
                color: vPipe.color, dashArray: '20', className: 'route-line',
            },
            {
                color: vPipe.color, dashArray: null, className: '',
            }]);

        return { pointCallbacks, pipeCoords, highlightPipeCallback };
    }

    const handleHighlight = async (pipe, isContinuous) => {
        editor.unregisterAllLoadingCallback();
        onView();
        const { pointCallbacks, pipeCoords, highlightPipeCallback } = await highlightPipeCallbacks(isContinuous, pipe);
        if (map) {
            editor.registerLoadingCallback(highlightPipeCallback);
            editor.registerMultipleLoadingCallbacks(pointCallbacks);

            map.highlightedGeometry = {
                bounds: pipeCoords, center: null,
            };
            map.panInsideBounds(pipeCoords);
        }
        editor.reloadFeatures();

        onRowClick(`pipe-${pipe.id}`);
    };

    const connectPipeTitle = (
        <FormattedMessage
            id="connect-pipe-to-pipe-title"
            defaultMessage="Connect pipe {ref}"
            values={{
                ref: connectingPipe?.ref,
            }}
        />
    );

    React.useEffect(() => {
        if (connectingPipe) {
            openPanel(PIPE_CONNECT_KEY, connectPipeTitle);
        }
    }, [connectingPipe, openPanel]);

    const handleConnectPipeClick = (pipe, isContinuous) => {
        setConnectingPipe({ pipe, isContinuous });
    };

    const handleConnectClose = () => {
        setConnectingPipe(null);
        closePanel(PIPE_CONNECT_KEY, false);
        onView();
    };

    const handleConnectSave = (selectedPipe) => {
        if (connectingPipe.isContinuous) {
            connectContinuousPipe(connectingPipe.pipe.uuid, { pipe: selectedPipe, point: point.uuid })
                .then(() => reload());
        } else {
            connectPipe(networkMap.uuid, connectingPipe.pipe.uuid, { pipe: selectedPipe, point: point.uuid })
                .then(() => reload());
        }

        setConnectingPipe(null);
        closePanel(PIPE_CONNECT_KEY, true);
    };

    const splitPipe = (pipe) => {
        split(pipe.uuid, { point: point.uuid }).then(() => reload());
    };

    return (
        <Box>
            <Spinner loading={loading || !loadedPoint11Data} />
            {point.type !== 'fork' && (
                <Box pad={{ bottom: 'small' }}>
                    <DisconnectedPipesList
                        pipes={disconnectedNonContinuousPipes}
                        continuousPipes={disconnectedContinuousPipes}
                        onConnectPipe={handleConnectPipeClick}
                        map={networkMap}
                        point11={point}
                        onRowClick={onRowClick}
                        clickedRow={clickedRow}
                        handleHighlight={handleHighlight}
                    />
                </Box>
            )}
            <TransitPipesList
                pipes={transitPipes}
                map={networkMap}
                point11={point}
                onRowClick={onRowClick}
                clickedRow={clickedRow}
                onSplitPipe={splitPipe}
                onPoint11View={onView}
                handleHighlight={handleHighlight}
            />
            {connectingPipe && (
                <PipeConnectSidepanel
                    pipe={connectingPipe}
                    onClose={handleConnectClose}
                    onSave={handleConnectSave}
                    pipes={disconnectedNonContinuousPipes}
                    continuousPipes={disconnectedContinuousPipes}
                    handleHighlight={highlightPipeCallbacks}
                    title={connectPipeTitle}
                />
            )}
        </Box>
    );
};

const apiCallback = (apiClient: ApiClient) => ({
    split: {
        send: (id: UUID, data: { point: UUID }) => apiClient.resources.continuousPipe.split(id, data),
        request: () => ({ loading: true }),
        success: () => ({ loading: false }),
        error: () => ({ loading: false }),
    },
    connectPipe: {
        send: (mapID: UUID, id: UUID, data: { pipe: UUID}) => apiClient.resources['pipe.geo'].connect(mapID, id, data),
        request: () => ({ loading: true }),
        success: () => ({ loading: false }),
        error: () => ({ loading: false }),
    },
    connectContinuousPipe: {
        send: (id: UUID, data: { pipe: UUID}) => apiClient.resources.continuousPipe.connect(id, data),
        request: () => ({ loading: true }),
        success: () => ({ loading: false }),
        error: () => ({ loading: false }),
    },
    getPipePath: {
        send: (id: string, map: Object) => {
            if (map.is_draft) {
                return apiClient.resources['pipe.geo']
                    .path(map.uuid, id, { is_draft: map.uuid });
            }
            return apiClient.resources['pipe.geo']
                .path(map.uuid, id);
        },
    },
    getContinuousPipePath: {
        send: (id: string, map: Object) => {
            if (map.is_draft) {
                return apiClient.resources.continuousPipe
                    .path(id, { is_draft: map.uuid });
            }
            return apiClient.resources.continuousPipe
                .path(id);
        },
    },
});

Point11PipesList.defaultProps = {
    loading: false,
};

const mapStateToProps = (state) => ({
    connectPanel: (() => getSidePanel(state, PIPE_CONNECT_KEY))(),
});

const mapDispatchToProps = (dispatch) => bindActionCreators(sidePanelActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(
    withLeaflet(
        connectAPI(apiCallback)(Point11PipesList),
    ),
);
